/**
 * Shared renderer for user-created slide templates – the single source of truth
 * for filling a compiled skeleton with AI-written content.
 *
 * Loaded in the browser as window.CustomTemplate (plain <script>) and
 * require()'d by utils/templates.js in Node (UMD, no DOM usage – pure string
 * building only), the same arrangement worksheet-render.js already uses.
 *
 * Math and escaping are injected rather than imported so this file never has to
 * work out which environment it's in:
 *
 *   CustomTemplate.render(compiled, fields, { renderTextWithMath, escapeHtml })
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.CustomTemplate = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    function renderSegments(segments, scope, io) {
        let out = '';
        for (const seg of segments) out += renderSegment(seg, scope, io);
        return out;
    }

    function renderSegment(seg, scope, io) {
        if (seg.k === 'lit') return seg.html;
        if (seg.k === 'rep') return renderRepeat(seg, scope, io);
        if (seg.k === 'slot') return renderSlot(seg, scope, io);
        return '';
    }

    function renderRepeat(seg, scope, io) {
        const rows = Array.isArray(scope[seg.name]) ? scope[seg.name] : [];
        let body = '';
        for (const row of rows) {
            body += renderSegments(seg.row, row && typeof row === 'object' ? row : {}, io);
        }
        // An empty group still renders its container: a visible gap is honest,
        // and it never leaks the designer's prototype row as fake content.
        return seg.open + body + seg.close;
    }

    function renderSlot(seg, scope, io) {
        const value = scope ? scope[seg.name] : undefined;

        if (seg.type === 'image') {
            const url = typeof value === 'string' ? value : '';
            if (!url) return seg.placeholder || '';
            const alt = typeof scope[seg.name + '_keyword'] === 'string' ? scope[seg.name + '_keyword'] : '';
            return seg.openPre
                + ' src="' + io.escapeHtml(url) + '"'
                + ' alt="' + io.escapeHtml(alt) + '"'
                + ' loading="lazy">';
        }

        if (seg.type === 'bullets') {
            const items = Array.isArray(value) ? value : [];
            if (!items.length && seg.optional) return '';
            let lis = '';
            for (const item of items) lis += seg.liOpen + io.renderTextWithMath(item) + seg.liClose;
            return seg.open + lis + seg.close;
        }

        const text = typeof value === 'string' ? value : '';
        if (!text && seg.optional) return '';
        return seg.open + io.renderTextWithMath(text) + seg.close;
    }

    /**
     * @param {object} compiled  slide_templates.compiled_json
     * @param {object} fields    AI-written content, keyed by slot name
     * @param {{renderTextWithMath: function, escapeHtml: function}} io
     * @returns {string} slide HTML
     */
    function render(compiled, fields, io) {
        if (!compiled || !Array.isArray(compiled.segments)) return '';
        return renderSegments(compiled.segments, fields || {}, io);
    }

    /**
     * Empty content for every slot, for inserting a template as a blank slide.
     */
    function blankFields(compiled) {
        const out = {};
        for (const slot of (compiled && compiled.slots) || []) {
            if (slot.type === 'bullets') out[slot.name] = [];
            else if (slot.type === 'repeat') out[slot.name] = [];
            else if (slot.type === 'image') out[slot.name] = '';
            else out[slot.name] = '';
        }
        return out;
    }

    return { render, blankFields };
}));
